import { forwardRef, memo, useState, useRef, useEffect } from 'react';

import { cn } from '../../lib/styles';

export type CodeInputSize = 'sm' | 'md';

export interface CodeInputProps {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  error?: React.ReactNode;
  /** Box/typography scale. Defaults to `md`. */
  size?: CodeInputSize;
  /**
   * Split the boxes into visual groups separated by a divider (e.g. `[3, 2, 4]`
   * for an SSN). Focus still advances continuously across all boxes. When set,
   * the total number of boxes is the sum of the groups and overrides `length`.
   */
  groups?: number[];
  containerClassName?: string;
  inputClassName?: string;
  className?: string;
  disabled?: boolean;
}

const SIZE_STYLES: Record<CodeInputSize, { gap: string; box: string; font: string }> = {
  md: { gap: 'gap-2', box: 'p-3 h-12 w-12', font: 'font-heading-2xl' },
  sm: { gap: 'gap-1.5', box: 'h-8 w-8', font: 'font-heading-lg' },
};

const CodeInputBase = (
  {
    className,
    length = 6,
    value = '',
    onChange,
    onComplete,
    error,
    size = 'md',
    groups,
    containerClassName,
    inputClassName,
    disabled = false,
    ...rest
  }: CodeInputProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const sizeStyles = SIZE_STYLES[size];
  const totalLength = groups ? groups.reduce((sum, group) => sum + group, 0) : length;
  const [localValue, setLocalValue] = useState(value);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleInputChange = (index: number, inputValue: string) => {
    if (disabled) return;

    const newValue = localValue.split('');
    newValue[index] = inputValue;
    const result = newValue.join('');

    setLocalValue(result);
    onChange?.(result);

    if (result.length === totalLength && onComplete) {
      onComplete(result);
      return;
    }

    if (inputValue && !localValue[index] && index < totalLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === 'Backspace' && !localValue[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (disabled) return;

    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, totalLength);
    if (pastedData.length > 0) {
      const result = pastedData.padEnd(totalLength, '');
      setLocalValue(result);
      onChange?.(result);

      if (pastedData.length === totalLength) {
        onComplete?.(pastedData);
        return;
      }

      const nextIndex = Math.min(pastedData.length, totalLength - 1);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const renderBox = (index: number) => (
    <input
      key={index}
      ref={(el) => {
        inputRefs.current[index] = el;
      }}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={1}
      value={localValue[index] || ''}
      onChange={(e) => handleInputChange(index, e.target.value)}
      onKeyDown={(e) => handleKeyDown(index, e)}
      aria-label={`Digit ${index + 1} of ${totalLength}`}
      className={cn(
        'font-semibold text-text-primary text-center',
        sizeStyles.font,
        sizeStyles.box,
        'outline-1 outline-bg-inter-secondary-default focus-visible:outline-border-input-select transition-all bg-bg-inter-secondary-default rounded-sm',
        disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
        inputClassName,
      )}
      autoComplete="one-time-code"
      disabled={disabled}
    />
  );

  const renderBoxes = () => {
    if (!groups) return Array.from({ length: totalLength }, (_, index) => renderBox(index));

    const boxes: React.ReactNode[] = [];
    let cursor = 0;
    groups.forEach((group, groupIndex) => {
      for (let i = 0; i < group; i += 1) {
        boxes.push(renderBox(cursor));
        cursor += 1;
      }
      if (groupIndex < groups.length - 1) {
        boxes.push(
          <span key={`sep-${groupIndex}`} className={cn('select-none text-text-tertiary', sizeStyles.font)}>
            -
          </span>,
        );
      }
    });
    return boxes;
  };

  return (
    <div className={cn('flex flex-col', className)}>
      <div
        ref={ref}
        className={cn('flex items-center justify-center', sizeStyles.gap, containerClassName)}
        onPaste={handlePaste}
        {...rest}
      >
        {renderBoxes()}
      </div>

      {error && <div className="h-5 pt-1 text-text-destructive font-body-sm text-center">{error}</div>}
    </div>
  );
};

export const CodeInput = memo(forwardRef(CodeInputBase));
